import React from 'react';

import AvatarBlock from '@wp-block-builder/blocks/Avatar';
import ButtonBlock from '@wp-block-builder/blocks/Button';
import { EditableBlock as ColumnsSectionBLock } from '@wp-block-builder/blocks/ColumnsSection';
import ContainerEditableBlock from '@wp-block-builder/blocks/Container/ContainerEditableBlock';
import DividerBlock from '@wp-block-builder/blocks/Divider';
import HeadingBlock from '@wp-block-builder/blocks/Heading';
import BlockPlaceholder from '@wp-block-builder/blocks/helpers/BlockPlaceholder';
import HtmlBlock from '@wp-block-builder/blocks/Html';
import ImageBlock from '@wp-block-builder/blocks/Image';
import LoopContainerEditableBlock from '@wp-block-builder/blocks/LoopContainer/LoopContainerEditableBlock';
import ProgressBarBlock from '@wp-block-builder/blocks/ProgressBar';
import SpacerBlock from '@wp-block-builder/blocks/Spacer';
import TextBlock from '@wp-block-builder/blocks/Text';
import buildBlockComponent from '@wp-block-builder/email-builder/buildBlockComponent';
import { TBlock } from '@wp-block-builder/package';

import BlockSectionWrapper from '../../document-layout/BlockSectionWrapper';
import { useEditableSectionStyle } from '../../document-layout/hooks';

import UiSectionWrapper from './UiSectionWrapper';

function BlockWrapper({ block, children }: { block: TBlock; children: JSX.Element }) {
  const blockStyle = useEditableSectionStyle(block);
  return <BlockSectionWrapper style={blockStyle}>{children}</BlockSectionWrapper>;
}

function ErrorSimplePlaceholder({ block }: { block: TBlock }) {
  return (
    <div>
      <BlockPlaceholder type={block.type} />
    </div>
  );
}

const EditableBlock = buildBlockComponent({
  Avatar: ({ block }) => (
    <UiSectionWrapper block={block} errorChildren={<ErrorSimplePlaceholder block={block} />}>
      <BlockWrapper block={block}>
        <AvatarBlock block={block} skipLiquid={false} />
      </BlockWrapper>
    </UiSectionWrapper>
  ),
  Button: ({ block }) => (
    <UiSectionWrapper block={block} errorChildren={<ErrorSimplePlaceholder block={block} />}>
      <BlockWrapper block={block}>
        <ButtonBlock block={block} skipLiquid={false} />
      </BlockWrapper>
    </UiSectionWrapper>
  ),
  ColumnsSection: ({ block }) => (
    <UiSectionWrapper block={block} errorChildren={<BlockPlaceholder type="ColumnsSection" />}>
      <BlockWrapper block={block}>
        <ColumnsSectionBLock block={block} BlockComponent={EditableBlock} />
      </BlockWrapper>
    </UiSectionWrapper>
  ),
  Container: ({ block }) => (
    <UiSectionWrapper block={block} errorChildren={<ErrorSimplePlaceholder block={block} />}>
      <BlockWrapper block={block}>
        <ContainerEditableBlock block={block} BlockComponent={EditableBlock} />
      </BlockWrapper>
    </UiSectionWrapper>
  ),
  ContainerYield: ({ block }) => (
    <UiSectionWrapper block={block} errorChildren={<ErrorSimplePlaceholder block={block} />}>
      <BlockPlaceholder type="ContainerYield" />
    </UiSectionWrapper>
  ),
  Divider: ({ block }) => (
    <UiSectionWrapper block={block} errorChildren={<ErrorSimplePlaceholder block={block} />}>
      <BlockWrapper block={block}>
        <DividerBlock block={block} />
      </BlockWrapper>
    </UiSectionWrapper>
  ),
  Heading: ({ block }) => (
    <UiSectionWrapper block={block} errorChildren={<ErrorSimplePlaceholder block={block} />}>
      <BlockWrapper block={block}>
        <HeadingBlock block={block} placeholder={<BlockPlaceholder type="Heading" />} skipLiquid={false} />
      </BlockWrapper>
    </UiSectionWrapper>
  ),
  Html: ({ block }) => (
    <UiSectionWrapper block={block} errorChildren={<ErrorSimplePlaceholder block={block} />}>
      <BlockWrapper block={block}>
        <HtmlBlock block={block} placeholder={<BlockPlaceholder type="Html" />} skipLiquid={false} />
      </BlockWrapper>
    </UiSectionWrapper>
  ),
  Image: ({ block }) => (
    <UiSectionWrapper block={block} errorChildren={<ErrorSimplePlaceholder block={block} />}>
      <BlockWrapper block={block}>
        <ImageBlock block={block} placeholder={<BlockPlaceholder type="Image" />} skipLiquid={false} />
      </BlockWrapper>
    </UiSectionWrapper>
  ),
  LoopContainer: ({ block }) => (
    <UiSectionWrapper block={block} errorChildren={<ErrorSimplePlaceholder block={block} />}>
      <BlockWrapper block={block}>
        <LoopContainerEditableBlock block={block} BlockComponent={EditableBlock} />
      </BlockWrapper>
    </UiSectionWrapper>
  ),
  ProgressBar: ({ block }) => (
    <UiSectionWrapper block={block} errorChildren={<ErrorSimplePlaceholder block={block} />}>
      <BlockWrapper block={block}>
        <ProgressBarBlock block={block} />
      </BlockWrapper>
    </UiSectionWrapper>
  ),
  Spacer: ({ block }) => (
    <UiSectionWrapper block={block} errorChildren={<ErrorSimplePlaceholder block={block} />}>
      <BlockWrapper block={block}>
        <SpacerBlock block={block} />
      </BlockWrapper>
    </UiSectionWrapper>
  ),
  Text: ({ block }) => (
    <UiSectionWrapper
      block={block}
      errorChildren={
        <div>
          <div>{block.props?.text || ''}</div>
        </div>
      }
    >
      <BlockWrapper block={block}>
        <TextBlock block={block} placeholder={<BlockPlaceholder type="Text" />} />
      </BlockWrapper>
    </UiSectionWrapper>
  ),
});

export default EditableBlock;
